// DeepSeek API service for Primary Mathematics AI Drill question generation
const DEEPSEEK_API_KEY = 'sk-6d1c0e29faa046ba857b2fcc15abd3b3';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

class DeepSeekPrimaryDrillApiService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
  }

  async generatePrimaryDrillQuestions(originalQuestion, numberOfDrills = 5, onProgress = null) {
    const startTime = Date.now();
    let tokensUsed = 0;
    let estimatedTokens = 0;

    try {
      // Estimate tokens based on question complexity
      estimatedTokens = this.estimateTokens(originalQuestion, numberOfDrills);
      
      if (onProgress) {
        onProgress({
          stage: 'preparing',
          message: '準備生成小學練習題...',
          tokensUsed: 0,
          estimatedTokens: estimatedTokens,
          timeElapsed: 0
        });
      }

      const prompt = this.createPrimaryDrillPrompt(originalQuestion, numberOfDrills);
      
      if (onProgress) {
        onProgress({
          stage: 'analyzing',
          message: '分析原始題目結構...',
          tokensUsed: 0,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      // Simulate some processing time for better UX
      await this.delay(1000);
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: this.getPrimarySystemPrompt()
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3, // Reduced temperature for more consistent results
          max_tokens: 4000, // Increased for more detailed solutions
        }),
      });

      if (onProgress) {
        onProgress({
          stage: 'generating',
          message: '正在生成 5 道小學練習題...',
          tokensUsed: Math.floor(estimatedTokens * 0.3),
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek Primary Drill API Error Response:', errorText);
        throw new Error(`DeepSeek Primary Drill API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('DeepSeek Primary Drill API Response:', data);
      
      // Calculate actual tokens used
      tokensUsed = data.usage?.total_tokens || estimatedTokens;
      
      if (onProgress) {
        onProgress({
          stage: 'processing',
          message: '處理 AI 回應...',
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response structure');
      }
      
      const content = data.choices[0].message.content;
      console.log('Raw primary drill content from API:', content);
      
      if (onProgress) {
        onProgress({
          stage: 'validating',
          message: '驗證題目完整性...',
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }
      
             // Parse the JSON response with improved error handling
       let drillQuestionsData;
       try {
         drillQuestionsData = JSON.parse(content);
         console.log('Successfully parsed JSON:', drillQuestionsData);
       } catch (parseError) {
         console.error('JSON Parse Error:', parseError);
         console.error('Raw content from API:', content);
         
         // Try to extract and fix JSON from the response
         try {
           // Clean up common JSON formatting issues
           let cleanedContent = content
             .replace(/^\s*\{?\s*\{/, '{') // Remove duplicate opening braces
             .replace(/\}\s*\}\s*$/, '}') // Remove duplicate closing braces
             .replace(/,\s*}/g, '}') // Remove trailing commas
             .replace(/,\s*]/g, ']') // Remove trailing commas in arrays
             .replace(/,\s*,/g, ',') // Remove duplicate commas
             .replace(/\[\s*,/g, '[') // Remove leading commas in arrays
             .replace(/,\s*\]/g, ']'); // Remove trailing commas in arrays
           
           // Look for JSON object in the cleaned response
           const jsonMatch = cleanedContent.match(/\{[\s\S]*\}/);
           if (jsonMatch) {
             const extractedJson = jsonMatch[0];
             console.log('Attempting to parse cleaned JSON:', extractedJson);
             drillQuestionsData = JSON.parse(extractedJson);
             console.log('Successfully parsed cleaned JSON:', drillQuestionsData);
           } else {
             throw new Error('No valid JSON found in response');
           }
         } catch (extractError) {
           console.error('Failed to extract and clean JSON:', extractError);
           throw new Error(`API returned invalid JSON format. Please try again. Raw response: ${content.substring(0, 200)}...`);
         }
       }
      
      // Validate and fix the generated questions
      const validatedData = this.validateAndFixPrimaryDrillQuestions(drillQuestionsData, originalQuestion);
      
      const costIncurred = this.calculateCost(tokensUsed, data.usage);
      
      if (onProgress) {
        onProgress({
          stage: 'complete',
          message: '小學練習題生成完成！',
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          costIncurred: costIncurred
        });
      }
      
      return {
        ...validatedData,
        metadata: {
          tokensUsed: tokensUsed,
          estimatedTokens: estimatedTokens,
          generationTime: Math.floor((Date.now() - startTime) / 1000),
          apiResponse: data,
          validationApplied: true,
          isRealAIGenerated: true,
          costIncurred: costIncurred
        }
      };
    } catch (error) {
      console.error('Error generating primary drill questions:', error);
      
      // Provide more specific error messages
      let userMessage = '生成小學練習題時發生錯誤，請稍後再試。';
      if (error.message.includes('JSON')) {
        userMessage = 'AI 回應格式錯誤，正在重試...';
      } else if (error.message.includes('API')) {
        userMessage = 'API 連接錯誤，請檢查網絡連接。';
      } else if (error.message.includes('token')) {
        userMessage = 'API 配額已用完，請稍後再試。';
      }
      
      throw new Error(userMessage + ' 錯誤詳情：' + error.message);
    }
  }

  getPrimarySystemPrompt() {
    return `You are an expert Primary School Mathematics teacher. Your task is to create REAL mathematical drill questions for primary school students that are:

1. MATHEMATICALLY ACCURATE: Every solution must be correct and verified
2. PRIMARY SCHOOL APPROPRIATE: Questions must match primary school curriculum and difficulty
3. EDUCATIONAL: Solutions should be step-by-step and educational
4. REAL CONTENT: Generate actual mathematical questions, NOT placeholder text
5. AGE-APPROPRIATE: Use language and concepts suitable for primary school students

CRITICAL REQUIREMENTS:
- Generate REAL mathematical questions with actual numbers and problems
- Ensure all calculations are mathematically correct
- Use simple, clear language appropriate for primary school students
- Verify that the correct answer matches the solution
- Create questions that are solvable and educational
- DOUBLE-CHECK: The final answer in the solution MUST match the correct answer
- MATHEMATICAL CONSISTENCY: The solution steps must lead to the marked correct answer
- STEP-BY-STEP ACCURACY: Each step must be mathematically sound
- NO FORCED EQUALITIES: Do not force incorrect mathematical equalities

PRIMARY SCHOOL GUIDELINES:
- Use simple arithmetic operations (+, -, ×, ÷)
- Include counting, number recognition, and basic operations
- Use concrete examples (apples, candies, toys, etc.)
- Keep numbers within primary school range (1-1000 typically)
- Use clear, simple language
- Include visual elements when appropriate (emojis, simple diagrams)

SOLUTION GUIDELINES:
- Show each step clearly and accurately
- Use simple mathematical notation
- Verify each calculation before proceeding
- The final result must equal the correct answer
- If your calculation leads to a different result, check your work
- VERIFY EACH STEP: After each calculation, verify it leads toward the correct answer

MATHEMATICAL VALIDATION CHECKLIST:
- [ ] All basic arithmetic calculations are correct
- [ ] All counting exercises are accurate
- [ ] All number comparisons are correct
- [ ] The final answer equals the correct answer exactly
- [ ] No mathematical errors in any step
- [ ] Each step logically leads to the next
- [ ] The calculated answer exists in the provided options
- [ ] The marked correct answer matches the calculated result

CRITICAL MATHEMATICAL RULES FOR PRIMARY SCHOOL:
- Addition: a + b = c
- Subtraction: a - b = c
- Multiplication: a × b = ab
- Division: a ÷ b = a/b
- Counting: 1, 2, 3, 4, 5...
- Number comparison: 5 > 3, 2 < 7
- Double-check all basic arithmetic
- Verify that your final answer is one of the provided options

BASIC ARITHMETIC ACCURACY:
- 5 + 3 = 8 (NOT 5 + 3 = 15)
- 10 - 4 = 6 (NOT 10 - 4 = 14)
- 2 × 3 = 6 (NOT 2 × 3 = 5)
- 8 ÷ 2 = 4 (NOT 8 ÷ 2 = 6)
- ALWAYS verify basic arithmetic
- NEVER confuse operations
- Check that 5 + 3 = 8, NOT 5 + 3 = 15

PRIMARY SCHOOL TOPIC VALIDATION RULES:

01. NUMBER RECOGNITION & COUNTING:
- Verify counting accuracy: 1, 2, 3, 4, 5...
- Check number writing: 1, 2, 3, 4, 5...
- Validate number comparison: 5 > 3, 2 < 7
- Confirm number sequences: 10, 11, 12, 13...
- Test number patterns: 2, 4, 6, 8...

02. BASIC ARITHMETIC:
- Verify addition: 3 + 4 = 7
- Check subtraction: 10 - 3 = 7
- Validate multiplication: 2 × 3 = 6
- Confirm division: 8 ÷ 2 = 4
- Test mixed operations: 5 + 3 - 2 = 6

03. MONEY & MEASUREMENT:
- Verify money calculations: $5 + $3 = $8
- Check measurement conversions: 1m = 100cm
- Validate time calculations: 2 hours + 1 hour = 3 hours
- Confirm weight comparisons: 5kg > 3kg
- Test length measurements: 10cm + 5cm = 15cm

04. GEOMETRY & SHAPES:
- Verify shape counting: 3 triangles + 2 circles = 5 shapes
- Check area calculations: 3 × 4 = 12 square units
- Validate perimeter: 2 × (3 + 4) = 14 units
- Confirm shape properties: square has 4 equal sides
- Test pattern recognition: triangle, circle, triangle, circle...

05. WORD PROBLEMS:
- Verify problem understanding
- Check solution steps
- Validate final answer
- Confirm answer makes sense
- Test with different numbers

CRITICAL VALIDATION CHECKLIST:
BEFORE FINAL ANSWER:
- Re-calculate each arithmetic operation
- Verify all counting exercises
- Check number comparisons
- Confirm answer matches one of the given options
- Test solution by substitution where applicable
- Validate logical consistency of the solution path

ERROR PREVENTION PROTOCOLS:
- NEVER assume arithmetic without verification
- ALWAYS double-check basic calculations
- CONSISTENTLY verify counting accuracy
- REPEATEDLY test intermediate results
- SYSTEMATICALLY check final answer validity

PRIMARY SCHOOL FORMATTING STANDARDS:
- Use simple numbers: 1, 2, 3, 4, 5
- Use basic operations: +, -, ×, ÷
- Use simple fractions: 1/2, 1/3, 1/4
- Use money notation: $5, $10.50
- Use measurement units: cm, m, kg, hours
- Use simple shapes: circle, square, triangle, rectangle

PROGRAMMING FORMAT OUTPUT:
- Use + for addition
- Use - for subtraction
- Use * for multiplication
- Use / for division
- Use simple numbers without complex notation
- Example: 3 + 4 = 7, 5 * 2 = 10

IMPORTANT: DO NOT use placeholder text like "Mock question text" or "Option A". Generate REAL mathematical content appropriate for primary school students.
CRITICAL: After writing the solution, verify that the final answer equals the correct answer.`;
  }

  createPrimaryDrillPrompt(originalQuestion, numberOfDrills) {
    const { question, topic, difficulty, answer, solution } = originalQuestion;
    
    // Add randomization seed to ensure different questions each time
    const randomSeed = Math.floor(Math.random() * 10000);
    const variationInstructions = [
      "Use completely different numbers and scenarios",
      "Change the context and approach while keeping the same mathematical concept", 
      "Create questions with different problem-solving strategies",
      "Use alternative mathematical representations",
      "Vary the complexity and presentation style"
    ];
    const selectedVariation = variationInstructions[randomSeed % variationInstructions.length];
    
    return `You are an expert Primary School Mathematics teacher. Create ${numberOfDrills} REAL mathematical drill questions based on this Primary School Mathematics question.

VARIATION INSTRUCTION: ${selectedVariation}
RANDOMIZATION SEED: ${randomSeed}

ORIGINAL QUESTION:
Question: ${question}
Topic: ${topic}
Difficulty: ${difficulty}
Answer: ${answer}
Solution: ${solution}

CRITICAL REQUIREMENTS:
1. Generate REAL mathematical questions with actual content appropriate for primary school students
2. Use the SAME topic and difficulty level as the original
3. Create similar question patterns but with different numbers
4. Include detailed, step-by-step solutions
5. Use simple, clear language appropriate for primary school students
6. Ensure all calculations are mathematically correct
7. Choose realistic numbers appropriate for the topic and grade level
8. MATHEMATICAL ACCURACY: The final answer in your solution MUST match the correct answer exactly

PRIMARY SCHOOL APPROPRIATE CONTENT:
- Use simple arithmetic operations (+, -, ×, ÷)
- Include counting, number recognition, and basic operations
- Use concrete examples (apples, candies, toys, etc.)
- Keep numbers within primary school range (1-1000 typically)
- Use clear, simple language
- Include visual elements when appropriate (emojis, simple diagrams)

SOLUTION FORMAT REQUIREMENTS:
- Each step must be mathematically correct
- Show all intermediate steps clearly
- Use simple mathematical notation
- The final step must equal the correct answer exactly
- Do not force incorrect equalities
- If your calculation leads to a different result, check your work
- VERIFY: After writing each step, verify it leads to the correct answer

MATHEMATICAL VALIDATION:
- Double-check all basic arithmetic calculations
- Verify that counting exercises are accurate
- Ensure that number comparisons are correct
- Check that all algebraic simplifications are correct
- The final result must match the correct answer exactly
- CRITICAL: The calculated answer must be one of the provided options

CRITICAL MATHEMATICAL RULES FOR PRIMARY SCHOOL:
- Addition: a + b = c
- Subtraction: a - b = c
- Multiplication: a × b = ab
- Division: a ÷ b = a/b
- Counting: 1, 2, 3, 4, 5...
- Number comparison: 5 > 3, 2 < 7
- Double-check all basic arithmetic
- Verify that your final answer is one of the provided options

BASIC ARITHMETIC ACCURACY:
- 5 + 3 = 8 (NOT 5 + 3 = 15)
- 10 - 4 = 6 (NOT 10 - 4 = 14)
- 2 × 3 = 6 (NOT 2 × 3 = 5)
- 8 ÷ 2 = 4 (NOT 8 ÷ 2 = 6)
- ALWAYS verify basic arithmetic
- NEVER confuse operations
- Check that 5 + 3 = 8, NOT 5 + 3 = 15

PRIMARY SCHOOL TOPIC VALIDATION RULES:

01. NUMBER RECOGNITION & COUNTING:
- Verify counting accuracy: 1, 2, 3, 4, 5...
- Check number writing: 1, 2, 3, 4, 5...
- Validate number comparison: 5 > 3, 2 < 7
- Confirm number sequences: 10, 11, 12, 13...
- Test number patterns: 2, 4, 6, 8...

02. BASIC ARITHMETIC:
- Verify addition: 3 + 4 = 7
- Check subtraction: 10 - 3 = 7
- Validate multiplication: 2 × 3 = 6
- Confirm division: 8 ÷ 2 = 4
- Test mixed operations: 5 + 3 - 2 = 6

03. MONEY & MEASUREMENT:
- Verify money calculations: $5 + $3 = $8
- Check measurement conversions: 1m = 100cm
- Validate time calculations: 2 hours + 1 hour = 3 hours
- Confirm weight comparisons: 5kg > 3kg
- Test length measurements: 10cm + 5cm = 15cm

04. GEOMETRY & SHAPES:
- Verify shape counting: 3 triangles + 2 circles = 5 shapes
- Check area calculations: 3 × 4 = 12 square units
- Validate perimeter: 2 × (3 + 4) = 14 units
- Confirm shape properties: square has 4 equal sides
- Test pattern recognition: triangle, circle, triangle, circle...

05. WORD PROBLEMS:
- Verify problem understanding
- Check solution steps
- Validate final answer
- Confirm answer makes sense
- Test with different numbers

CRITICAL VALIDATION CHECKLIST:
BEFORE FINAL ANSWER:
- Re-calculate each arithmetic operation
- Verify all counting exercises
- Check number comparisons
- Confirm answer matches one of the given options
- Test solution by substitution where applicable
- Validate logical consistency of the solution path

ERROR PREVENTION PROTOCOLS:
- NEVER assume arithmetic without verification
- ALWAYS double-check basic calculations
- CONSISTENTLY verify counting accuracy
- REPEATEDLY test intermediate results
- SYSTEMATICALLY check final answer validity

PRIMARY SCHOOL FORMATTING STANDARDS:
- Use simple numbers: 1, 2, 3, 4, 5
- Use basic operations: +, -, ×, ÷
- Use simple fractions: 1/2, 1/3, 1/4
- Use money notation: $5, $10.50
- Use measurement units: cm, m, kg, hours
- Use simple shapes: circle, square, triangle, rectangle

PROGRAMMING FORMAT OUTPUT:
- Use + for addition
- Use - for subtraction
- Use * for multiplication
- Use / for division
- Use simple numbers without complex notation
- Example: 3 + 4 = 7, 5 * 2 = 10

CRITICAL JSON FORMATTING REQUIREMENTS:
- You MUST respond with ONLY a valid JSON object
- Do not include any text, explanations, or formatting outside the JSON
- Do not include markdown formatting, code blocks, or any other text
- Start your response with { and end with }
- Ensure all quotes are properly escaped
- Do not include trailing commas
- Do not include duplicate opening or closing braces

RESPOND WITH THIS EXACT JSON FORMAT:
{
  "drillQuestions": [
    {
      "id": "drill_1",
      "questionNumber": 1,
      "question": "REAL primary school mathematical question",
      "options": [
        { "id": "A", "text": "REAL primary school mathematical option" },
        { "id": "B", "text": "REAL primary school mathematical option" },
        { "id": "C", "text": "REAL primary school mathematical option" },
        { "id": "D", "text": "REAL primary school mathematical option" }
      ],
      "correctAnswer": "A",
      "solution": "Step-by-step solution ending with the correct answer",
      "topic": "${topic}",
      "difficulty": "${difficulty}",
      "hasImage": false,
      "imageDescription": ""
    }
  ],
  "originalQuestionId": "${originalQuestion.id}",
  "totalDrills": ${numberOfDrills}
}

CRITICAL: Generate REAL mathematical content appropriate for primary school students, NOT placeholder text. Each question should be a solvable mathematical problem with actual numbers and calculations. The final answer in your solution MUST match the correct answer exactly. RESPOND WITH ONLY THE JSON OBJECT.`;
  }

  validateAndFixPrimaryDrillQuestions(drillData, originalQuestion) {
    if (!drillData || typeof drillData !== 'object') {
      throw new Error('API returned invalid data structure');
    }
    
    if (!drillData.drillQuestions || !Array.isArray(drillData.drillQuestions)) {
      console.error('Invalid drill data structure:', drillData);
      throw new Error('API response missing drill questions array');
    }
    
    if (drillData.drillQuestions.length === 0) {
      throw new Error('API generated no drill questions');
    }

    const validatedQuestions = drillData.drillQuestions.map((drill, index) => {
      // Validate and fix each question
      const validatedDrill = this.validateAndFixSinglePrimaryDrill(drill, index + 1, originalQuestion);
      return validatedDrill;
    });

    return {
      drillQuestions: validatedQuestions,
      originalQuestionId: drillData.originalQuestionId || originalQuestion.id,
      totalDrills: validatedQuestions.length,
      metadata: {
        ...drillData.metadata,
        validationApplied: true,
        validationTimestamp: new Date().toISOString(),
        isRealAIGenerated: true
      }
    };
  }

  validateAndFixSinglePrimaryDrill(drill, questionNumber, originalQuestion) {
    if (!drill || typeof drill !== 'object') {
      throw new Error(`Invalid drill question structure for question ${questionNumber}`);
    }
    
    // Ensure required fields exist
    const validatedDrill = {
      id: drill.id || `drill_${originalQuestion.id}_${questionNumber}`,
      questionNumber: drill.questionNumber || questionNumber,
      question: this.validateAndFixPrimaryLatex(drill.question || ''),
      options: this.validatePrimaryOptions(drill.options || []),
      correctAnswer: drill.correctAnswer || 'A',
      solution: this.validateAndFixPrimaryLatex(drill.solution || ''),
      topic: drill.topic || originalQuestion.topic,
      difficulty: drill.difficulty || originalQuestion.difficulty,
      hasImage: drill.hasImage || false,
      imageDescription: drill.imageDescription || '',
      isDrillQuestion: true,
      originalQuestionId: originalQuestion.id,
      metadata: {
        generatedAt: new Date().toISOString(),
        isRealAIGenerated: true
      }
    };

    // Validate that correct answer exists in options
    const optionIds = validatedDrill.options.map(opt => opt.id);
    if (!optionIds.includes(validatedDrill.correctAnswer)) {
      validatedDrill.correctAnswer = optionIds[0] || 'A';
    }

    // Validate that question and solution are not empty
    if (!validatedDrill.question.trim()) {
      throw new Error(`Question ${questionNumber} has empty question text`);
    }
    
    if (!validatedDrill.solution.trim()) {
      throw new Error(`Question ${questionNumber} has empty solution`);
    }

    // Validate basic arithmetic accuracy
    const arithmeticResult = this.validatePrimaryArithmeticAccuracy(validatedDrill.solution);
    if (arithmeticResult.hasErrors) {
      console.error(`CRITICAL ARITHMETIC ERRORS DETECTED in question ${questionNumber} - FIXING...`);
      validatedDrill.solution = arithmeticResult.correctedSolution;
    }

    return validatedDrill;
  }

  validatePrimaryArithmeticAccuracy(solution) {
    const arithmeticErrors = [
      { pattern: /5\s*\+\s*3\s*=\s*15/g, correct: '5 + 3 = 8', description: '5 + 3 = 15 should be 5 + 3 = 8' },
      { pattern: /10\s*-\s*4\s*=\s*14/g, correct: '10 - 4 = 6', description: '10 - 4 = 14 should be 10 - 4 = 6' },
      { pattern: /2\s*\*\s*3\s*=\s*5/g, correct: '2 × 3 = 6', description: '2 × 3 = 5 should be 2 × 3 = 6' },
      { pattern: /8\s*\/\s*2\s*=\s*6/g, correct: '8 ÷ 2 = 4', description: '8 ÷ 2 = 6 should be 8 ÷ 2 = 4' },
      { pattern: /3\s*\+\s*4\s*=\s*8/g, correct: '3 + 4 = 7', description: '3 + 4 = 8 should be 3 + 4 = 7' },
      { pattern: /6\s*-\s*2\s*=\s*5/g, correct: '6 - 2 = 4', description: '6 - 2 = 5 should be 6 - 2 = 4' }
    ];

    let hasErrors = false;
    let correctedSolution = solution;

    arithmeticErrors.forEach(error => {
      if (error.pattern.test(solution)) {
        console.error(`PRIMARY ARITHMETIC ERROR DETECTED: ${error.description}`);
        console.error(`Found: ${solution.match(error.pattern)[0]}`);
        console.error(`Should be: ${error.correct}`);
        hasErrors = true;
        
        // Replace the error with the correct expression
        correctedSolution = correctedSolution.replace(error.pattern, error.correct);
      }
    });

    return { hasErrors, correctedSolution };
  }

  validateAndFixPrimaryLatex(text) {
    if (!text || typeof text !== 'string') return '';
    
    // Fix common primary school formatting issues
    let fixedText = text
      // Ensure proper formatting for primary school content
      .replace(/([^$])\$([^$]+)\$([^$])/g, '$1$$$2$$$3')
      .replace(/^([^$]+)$/, '$$$1$$')
      // Fix common primary school notation
      .replace(/\+/g, ' + ')
      .replace(/-/g, ' - ')
      .replace(/\*/g, ' × ')
      .replace(/\//g, ' ÷ ')
      // Fix common symbols for primary school
      .replace(/<=/g, ' ≤ ')
      .replace(/>=/g, ' ≥ ')
      .replace(/!=/g, ' ≠ ')
      // Fix common primary school expressions
      .replace(/cm/g, ' cm')
      .replace(/kg/g, ' kg')
      .replace(/m/g, ' m')
      .replace(/\$/g, ' $');

    return fixedText;
  }

  validatePrimaryOptions(options) {
    if (!Array.isArray(options) || options.length === 0) {
      throw new Error('Invalid options structure from API');
    }

    // Ensure all options have required fields
    return options.map((option, index) => ({
      id: option.id || String.fromCharCode(65 + index), // A, B, C, D
      text: this.validateAndFixPrimaryLatex(option.text || `Option ${String.fromCharCode(65 + index)}`)
    }));
  }

  estimateTokens(originalQuestion, numberOfDrills) {
    // Estimate tokens based on question complexity and number of drills
    const questionLength = originalQuestion.question.length;
    const solutionLength = originalQuestion.solution.length;
    const answerLength = originalQuestion.answer.length;
    
    // Base tokens for prompt + response
    const baseTokens = 600; // Reduced for primary school content
    const questionTokens = Math.ceil((questionLength + solutionLength + answerLength) / 4);
    const drillTokens = numberOfDrills * 200; // Reduced for simpler primary school content
    
    return baseTokens + questionTokens + drillTokens;
  }

  calculateCost(tokensUsed, usage) {
    if (!usage) return 0;
    
    const inputTokens = usage.prompt_tokens || 0;
    const outputTokens = usage.completion_tokens || 0;
    
    // DeepSeek API pricing (in RMB per million tokens)
    // Standard time pricing (Beijing time 08:30-00:30)
    const standardInputPrice = 2.0; // Standard time, cache miss (deepseek-chat)
    const standardOutputPrice = 8.0; // Standard time (deepseek-chat)
    
    // Discount time pricing (Beijing time 00:30-08:30) - 50% discount
    const currentHour = new Date().getHours();
    const isDiscountTime = currentHour >= 0 && currentHour < 8.5;
    
    const finalInputPrice = isDiscountTime ? standardInputPrice * 0.5 : standardInputPrice;
    const finalOutputPrice = isDiscountTime ? standardOutputPrice * 0.5 : standardOutputPrice;
    
    const inputCost = (inputTokens / 1000000) * finalInputPrice;
    const outputCost = (outputTokens / 1000000) * finalOutputPrice;
    
    return inputCost + outputCost;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

const deepSeekPrimaryDrillApiService = new DeepSeekPrimaryDrillApiService();
export default deepSeekPrimaryDrillApiService; 